/**
 * Inputs: stay id. Outputs: owner updates to place, stay notes/windows, and room booking details.
 * Cost and supplier payments stay on the booking money screen.
 */

import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { formatDateLabel, todayInZone } from "@shared/time.ts";
import { bookingDisplayTitle } from "@shared/upcoming.ts";
import { MAPS_URL_COORD_SOURCE } from "@shared/maps-url.ts";
import type { Booking, Stay } from "@shared/schema.ts";
import { useSession } from "../../app/session.tsx";
import { useTrip } from "../../app/trip.tsx";
import { SmartBack } from "../../components/SmartBack.tsx";
import { PlaceLinkFields } from "./PlaceLinkFields.tsx";
import { type PlaceDraftFields } from "@shared/maps-place.ts";

function notesToText(notes: string[] | undefined): string {
  return (notes ?? []).join("\n");
}

function textToNotes(value: string): string[] {
  return value.split("\n").map((row) => row.trim()).filter(Boolean);
}

function occupancyText(booking: Booking): { adults: string; children: string } {
  return {
    adults: booking.occupancy ? String(booking.occupancy.adults) : "",
    children: booking.occupancy ? String(booking.occupancy.children) : "",
  };
}

export function StayEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useSession();
  const { trip, save, saveState } = useTrip();
  const stay = trip?.stays.find((row) => row.id === id);
  const place = trip?.places.find((row) => row.id === stay?.placeId);
  const rooms = trip && stay
    ? trip.bookings.filter((row) => row.stayId === stay.id).slice().sort((a, b) =>
      (a.startDate ?? "").localeCompare(b.startDate ?? "") || (a.householdId ?? "").localeCompare(b.householdId ?? ""))
    : [];

  const [placeDraft, setPlaceDraft] = useState<PlaceDraftFields>(() => ({
    name: place?.name ?? "",
    mapsUrl: place?.mapsUrl ?? "",
    address: place?.address ?? "",
    thaiName: place?.thaiName ?? "",
    coordinates: place?.coordinates
      ? { lat: place.coordinates.lat, lng: place.coordinates.lng }
      : null,
    lookupStatus: "idle",
    lookupNote: null,
  }));
  const [checkInFrom, setCheckInFrom] = useState(stay?.checkInFrom ?? "");
  const [checkOutBy, setCheckOutBy] = useState(stay?.checkOutBy ?? "");
  const [plannedDeparture, setPlannedDeparture] = useState(stay?.plannedDepartureLocal?.slice(11, 16) ?? stay?.plannedDepartureLocal?.slice(0, 5) ?? "");
  const [stayNotes, setStayNotes] = useState(notesToText(stay?.notes));
  const [roomDrafts, setRoomDrafts] = useState(() =>
    Object.fromEntries(rooms.map((room) => [room.id, {
      roomName: room.roomName ?? "",
      confirmation: room.confirmation ?? "",
      expediaItinerary: room.expediaItinerary ?? "",
      adults: occupancyText(room).adults,
      children: occupancyText(room).children,
      breakfastIncluded: Boolean(room.breakfastIncluded),
      cancellationTerms: room.cancellationTerms ?? "",
      notes: notesToText(room.notes),
    }])),
  );
  const [message, setMessage] = useState<string | null>(null);

  if (role !== "owner") {
    return (
      <main className="page">
        <SmartBack fallback={stay ? `/journey/stay/${stay.id}` : "/journey"} />
        <h1 className="serif">Owner only</h1>
        <p>Family sessions can read everything but cannot edit.</p>
      </main>
    );
  }
  if (!trip || !stay || !place) {
    return (
      <main className="page">
        <SmartBack fallback="/journey" />
        <p>Stay not found.</p>
      </main>
    );
  }

  function updateRoom(roomId: string, patch: Partial<(typeof roomDrafts)[string]>) {
    setRoomDrafts((current) => ({ ...current, [roomId]: { ...current[roomId], ...patch } }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!trip || !stay || !place) return;
    const nextStay: Stay = {
      ...stay,
      checkInFrom: checkInFrom.trim() || null,
      checkOutBy: checkOutBy.trim() || null,
      plannedDepartureLocal: plannedDeparture.trim()
        ? (plannedDeparture.includes("T") ? plannedDeparture.trim() : `${stay.endDate}T${plannedDeparture.trim()}:00`)
        : stay.plannedDepartureLocal ?? null,
      notes: textToNotes(stayNotes),
    };
    const asOf = todayInZone(new Date(), trip.trip.diaryTimezone);
    const nextPlaces = trip.places.map((row) => row.id === place.id ? {
      ...row,
      address: placeDraft.address.trim() || null,
      mapsUrl: placeDraft.mapsUrl.trim() || null,
      thaiName: placeDraft.thaiName.trim() || null,
      coordinates: row.coordinates ?? (placeDraft.coordinates
        ? {
            lat: placeDraft.coordinates.lat,
            lng: placeDraft.coordinates.lng,
            verified: false,
            source: MAPS_URL_COORD_SOURCE,
            asOf,
          }
        : null),
    } : row);
    const nextBookings = trip.bookings.map((row) => {
      const draft = roomDrafts[row.id];
      if (!draft) return row;
      const adults = draft.adults.trim() === "" ? null : Number(draft.adults);
      const children = draft.children.trim() === "" ? null : Number(draft.children);
      const occupancy = adults != null && Number.isInteger(adults) && adults >= 0
        ? { adults, children: children != null && Number.isInteger(children) && children >= 0 ? children : 0 }
        : row.occupancy ?? null;
      return {
        ...row,
        roomName: draft.roomName.trim() || null,
        confirmation: draft.confirmation.trim() || null,
        expediaItinerary: draft.expediaItinerary.trim() || null,
        occupancy,
        breakfastIncluded: draft.breakfastIncluded,
        cancellationTerms: draft.cancellationTerms.trim() || null,
        notes: textToNotes(draft.notes),
      };
    });
    const result = await save({
      ...trip,
      stays: trip.stays.map((row) => (row.id === stay.id ? nextStay : row)),
      places: nextPlaces,
      bookings: nextBookings,
    });
    if (!result.ok) {
      setMessage(result.message);
      return;
    }
    navigate(`/journey/stay/${stay.id}`);
  }

  return (
    <main className="page">
      <SmartBack fallback={`/journey/stay/${stay.id}`} />
      <p className="kicker">{stay.chapter}</p>
      <h1 className="serif">Edit stay details</h1>
      <p className="muted">Notes, address, check-in and each room live here. Amounts and payments stay on the booking.</p>
      <form onSubmit={(e) => void onSubmit(e)}>
        <PlaceLinkFields
          value={placeDraft}
          onChange={setPlaceDraft}
          showName={false}
          mapLabel="Google Maps link"
        />
        <label className="field">
          Check-in from
          <input value={checkInFrom} onChange={(e) => setCheckInFrom(e.target.value)} placeholder="14:00" />
        </label>
        <label className="field">
          Official checkout
          <input value={checkOutBy} onChange={(e) => setCheckOutBy(e.target.value)} placeholder="12:00" />
        </label>
        <label className="field">
          Planned departure time
          <input value={plannedDeparture} onChange={(e) => setPlannedDeparture(e.target.value)} placeholder="08:10" />
        </label>
        <label className="field">
          Stay notes
          <textarea
            value={stayNotes}
            onChange={(e) => setStayNotes(e.target.value)}
            rows={5}
            placeholder="Wifi, extra beds, breakfast time, parking, anything useful at this hotel"
          />
        </label>

        {rooms.map((room) => {
          const draft = roomDrafts[room.id];
          if (!draft) return null;
          const household = trip.households.find((h) => h.id === room.householdId);
          return (
            <section key={room.id} className="card">
              <h2 className="serif">{household ? `${household.label} · ` : ""}{draft.roomName || bookingDisplayTitle(room, trip)}</h2>
              <p className="muted">
                {formatDateLabel(room.startDate ?? stay.startDate)} - {formatDateLabel(room.endDate ?? stay.endDate)}
              </p>
              <label className="field">
                Room name
                <input value={draft.roomName} onChange={(e) => updateRoom(room.id, { roomName: e.target.value })} />
              </label>
              <label className="field">
                Confirmation
                <input value={draft.confirmation} onChange={(e) => updateRoom(room.id, { confirmation: e.target.value })} />
              </label>
              <label className="field">
                Agent itinerary
                <input value={draft.expediaItinerary} onChange={(e) => updateRoom(room.id, { expediaItinerary: e.target.value })} />
              </label>
              <label className="field">
                Adults
                <input value={draft.adults} onChange={(e) => updateRoom(room.id, { adults: e.target.value })} inputMode="numeric" />
              </label>
              <label className="field">
                Children
                <input value={draft.children} onChange={(e) => updateRoom(room.id, { children: e.target.value })} inputMode="numeric" />
              </label>
              <label className="field" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 10 }}>
                <input
                  type="checkbox"
                  checked={draft.breakfastIncluded}
                  onChange={(e) => updateRoom(room.id, { breakfastIncluded: e.target.checked })}
                />
                Breakfast included
              </label>
              <label className="field">
                Cancellation terms
                <textarea value={draft.cancellationTerms} onChange={(e) => updateRoom(room.id, { cancellationTerms: e.target.value })} rows={2} />
              </label>
              <label className="field">
                Notes for this reservation
                <textarea value={draft.notes} onChange={(e) => updateRoom(room.id, { notes: e.target.value })} rows={3} placeholder="Bed type, extra request, anything from the confirmation email" />
              </label>
              <p><Link to={`/bookings/${room.id}`}>Cost and payment</Link></p>
            </section>
          );
        })}

        {message ? <p className="banner crit">{message}</p> : null}
        <button className="btn" type="submit" disabled={saveState === "saving"}>
          {saveState === "saving" ? "Saving…" : "Save stay details"}
        </button>
      </form>
    </main>
  );
}
